import * as React from 'react'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import type { State } from 'types/redux'
import { makeGetFeatureData } from '@disco/state-management/selectors/business'
import ContactUsLink from 'components/ui/contact-us-link'
import Lozenge from 'components/ui/lozenge'
import PricingPopover from 'components/ui/pricing-popover'
import styles from './index.module.css'

type OwnProps = {
  description?: string | React.ReactNode
  featureName?: string
  intercomMsg?: string
  title?: string
  onClick?: (...args: Array<any>) => any
}

function mapStateToProps(state: State, ownProps: OwnProps): any {
  const getFeatureData = makeGetFeatureData()
  const { badgeTitle, isTrial } = getFeatureData(state, {
    featureName: ownProps.featureName || '',
  })
  return {
    title: ownProps.title || badgeTitle,
    isTrial,
  }
}

/**
 * A badge component that displays pricing information with optional popover description and click handling
 * @param description - Optional description content to display in a popover when hovering over the badge
 * @param featureName - Name of the feature to get pricing data for
 * @param intercomMsg - Message to pass to Intercom for contact us functionality
 * @param title - Override title for the badge (defaults to badgeTitle from feature data)
 * @param onClick - Custom click handler for the badge
 * @param isTrial - Whether the user is in trial mode (from Redux state)
 * @returns Pricing badge with conditional popover and click behavior based on trial status
 */
class PricingBadge extends React.Component<
  OwnProps & {
    isTrial: boolean
  }
> {
  _onClick = e => {
    const { onClick } = this.props
    if (onClick) onClick()
  }

  _titleWrapper(node): React.ReactNode {
    const { onClick, isTrial, intercomMsg } = this.props
    const link = isTrial ? '/settings/billing' : ''

    if (!onClick && !isTrial) {
      return (
        <ContactUsLink className={styles.badgeTitle} intercomMsg={intercomMsg}>
          {node}
        </ContactUsLink>
      )
    }

    return (
      <Link to={link} className={styles.badgeTitle} onClick={this._onClick}>
        {node}
      </Link>
    )
  }

  render() {
    const { description, title } = this.props

    const badge = this._titleWrapper(
      <Lozenge color="outline-blue" text={title} />,
    )

    if (description) {
      return <PricingPopover description={description}>{badge}</PricingPopover>
    }

    return badge
  }
}

export default connect(mapStateToProps)(PricingBadge) as any
